/**
 * RepresentativesTab — Meghatalmazott kezelés (GAP-2)
 * Partner-szintű képviselők listája, létrehozás, szerkesztés, törlés, aktiválás.
 */

import {
  activateRepresentative,
  createRepresentative,
  deactivateRepresentative,
  deleteRepresentative,
  getRepresentatives,
  updateRepresentative,
  type CreateRepresentativeInput,
  type Representative,
} from '@/api/representatives';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// HELPERS
// ============================================

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('hu-HU');
}

function isExpired(validUntil: string | null): boolean {
  if (!validUntil) return false;
  return new Date(validUntil) < new Date();
}

function isExpiringSoon(validUntil: string | null): boolean {
  if (!validUntil) return false;
  const daysUntil = (new Date(validUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return daysUntil >= 0 && daysUntil <= 30;
}

function toDateInputValue(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().substring(0, 10);
}

// ============================================
// STATUS BADGE
// ============================================

function StatusBadge({ rep }: { rep: Representative }) {
  const { t } = useTranslation();

  if (isExpired(rep.validUntil)) {
    return (
      <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
        {t('partner.representative.expired')}
      </span>
    );
  }
  if (isExpiringSoon(rep.validUntil)) {
    return (
      <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
        {t('partner.representative.expiringSoon')}
      </span>
    );
  }
  if (!rep.isActive) {
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
        {t('partner.representative.inactive')}
      </span>
    );
  }
  return (
    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/20 dark:text-green-300">
      {t('partner.representative.active')}
    </span>
  );
}

// ============================================
// PERMISSION BADGES
// ============================================

function PermissionBadges({ rep }: { rep: Representative }) {
  const { t } = useTranslation();
  const perms: Array<{ key: keyof Representative; label: string }> = [
    { key: 'canRent', label: t('partner.representative.canRent') },
    { key: 'canReturn', label: t('partner.representative.canReturn') },
    { key: 'canSign', label: t('partner.representative.canSign') },
    { key: 'canPayCash', label: t('partner.representative.canPayCash') },
  ];

  const active = perms.filter(p => rep[p.key] === true);
  if (active.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {active.map(p => (
        <span
          key={p.key}
          className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary"
        >
          {p.label}
        </span>
      ))}
    </div>
  );
}

// ============================================
// ADD / EDIT FORM
// ============================================

interface RepFormState {
  name: string;
  phone: string;
  email: string;
  idNumber: string;
  validFrom: string;
  validUntil: string;
  isActive: boolean;
  canRent: boolean;
  canReturn: boolean;
  canSign: boolean;
  canPayCash: boolean;
  notes: string;
}

function emptyForm(today: string): RepFormState {
  return {
    name: '',
    phone: '',
    email: '',
    idNumber: '',
    validFrom: today,
    validUntil: '',
    isActive: true,
    canRent: true,
    canReturn: true,
    canSign: false,
    canPayCash: false,
    notes: '',
  };
}

function repToForm(rep: Representative): RepFormState {
  return {
    name: rep.name,
    phone: rep.phone ?? '',
    email: rep.email ?? '',
    idNumber: rep.idNumber ?? '',
    validFrom: toDateInputValue(rep.validFrom),
    validUntil: toDateInputValue(rep.validUntil),
    isActive: rep.isActive,
    canRent: rep.canRent,
    canReturn: rep.canReturn,
    canSign: rep.canSign,
    canPayCash: rep.canPayCash,
    notes: rep.notes ?? '',
  };
}

function formToInput(form: RepFormState): CreateRepresentativeInput {
  return {
    name: form.name.trim(),
    phone: form.phone.trim() || undefined,
    email: form.email.trim() || undefined,
    idNumber: form.idNumber.trim() || undefined,
    validFrom: form.validFrom,
    validUntil: form.validUntil || undefined,
    isActive: form.isActive,
    canRent: form.canRent,
    canReturn: form.canReturn,
    canSign: form.canSign,
    canPayCash: form.canPayCash,
    notes: form.notes.trim() || undefined,
  };
}

interface RepFormProps {
  initial: RepFormState;
  saving: boolean;
  onSave: (form: RepFormState) => void;
  onCancel: () => void;
}

function RepForm({ initial, saving, onSave, onCancel }: RepFormProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<RepFormState>(initial);

  const set = (field: keyof RepFormState, value: string | boolean) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const permissionFields: Array<{ key: keyof RepFormState; label: string }> = [
    { key: 'canRent', label: t('partner.representative.canRent') },
    { key: 'canReturn', label: t('partner.representative.canReturn') },
    { key: 'canSign', label: t('partner.representative.canSign') },
    { key: 'canPayCash', label: t('partner.representative.canPayCash') },
  ];

  return (
    <div className="space-y-4 rounded-lg border border-primary bg-background p-4 dark:border-primary/60">
      {/* Name — required */}
      <div>
        <label className="mb-1 block text-sm font-medium text-foreground">
          {t('partner.representative.name')} *
        </label>
        <Input
          value={form.name}
          onChange={e => set('name', e.target.value)}
          placeholder={t('partner.representative.name')}
          disabled={saving}
          autoFocus
        />
      </div>

      {/* Phone + Email */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            {t('partner.representative.phone')}
          </label>
          <Input
            value={form.phone}
            onChange={e => set('phone', e.target.value)}
            placeholder="+36 30 123 4567"
            disabled={saving}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            {t('partner.representative.email')}
          </label>
          <Input
            type="email"
            value={form.email}
            onChange={e => set('email', e.target.value)}
            placeholder="nev@pelda.hu"
            disabled={saving}
          />
        </div>
      </div>

      {/* ID Number */}
      <div>
        <label className="mb-1 block text-sm font-medium text-foreground">
          {t('partner.representative.idNumber')}
        </label>
        <Input
          value={form.idNumber}
          onChange={e => set('idNumber', e.target.value)}
          placeholder="123456AB"
          disabled={saving}
        />
      </div>

      {/* Validity */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            {t('partner.representative.validFrom')} *
          </label>
          <Input
            type="date"
            value={form.validFrom}
            onChange={e => set('validFrom', e.target.value)}
            disabled={saving}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            {t('partner.representative.validUntil')}
            <span className="ml-1 text-xs text-muted-foreground">
              ({t('partner.representative.unlimited')})
            </span>
          </label>
          <Input
            type="date"
            value={form.validUntil}
            onChange={e => set('validUntil', e.target.value)}
            disabled={saving}
          />
        </div>
      </div>

      {/* Permissions */}
      <div>
        <p className="mb-2 text-sm font-medium text-foreground">
          {t('partner.representative.permissions')}
        </p>
        <div className="flex flex-wrap gap-4">
          {permissionFields.map(pf => (
            <label key={pf.key} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form[pf.key] as boolean}
                onChange={e => set(pf.key, e.target.checked)}
                disabled={saving}
                className="accent-primary"
              />
              {pf.label}
            </label>
          ))}
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={e => set('isActive', e.target.checked)}
              disabled={saving}
              className="accent-primary"
            />
            {t('partner.representative.active')}
          </label>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="mb-1 block text-sm font-medium text-foreground">
          {t('partner.representative.notes')}
        </label>
        <Input
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder="..."
          disabled={saving}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={() => onSave(form)}
          disabled={saving || !form.name.trim() || !form.validFrom}
        >
          {t('common.save')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}

// ============================================
// REPRESENTATIVE ROW
// ============================================

interface RepRowProps {
  rep: Representative;
  partnerId: string;
  onChanged: () => void;
}

function RepRow({ rep, partnerId, onChanged }: RepRowProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleSave = useCallback(
    async (form: RepFormState) => {
      setSaving(true);
      try {
        await updateRepresentative(partnerId, rep.id, formToInput(form));
        setEditing(false);
        onChanged();
      } catch {
        // silent — in production wire up toast
      } finally {
        setSaving(false);
      }
    },
    [partnerId, rep.id, onChanged]
  );

  const handleDelete = useCallback(async () => {
    if (!window.confirm(t('partner.representative.confirmDelete'))) return;
    setDeleting(true);
    try {
      await deleteRepresentative(partnerId, rep.id);
      onChanged();
    } catch {
      // silent
    } finally {
      setDeleting(false);
    }
  }, [partnerId, rep.id, t, onChanged]);

  const handleToggleActive = useCallback(async () => {
    setToggling(true);
    try {
      if (rep.isActive) {
        await deactivateRepresentative(partnerId, rep.id);
      } else {
        await activateRepresentative(partnerId, rep.id);
      }
      onChanged();
    } catch {
      // silent
    } finally {
      setToggling(false);
    }
  }, [partnerId, rep.id, rep.isActive, onChanged]);

  if (editing) {
    return (
      <div className="p-4">
        <RepForm
          initial={repToForm(rep)}
          saving={saving}
          onSave={form => void handleSave(form)}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-start justify-between gap-3 p-4">
      {/* Main info */}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">{rep.name}</span>
          <StatusBadge rep={rep} />
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-muted-foreground">
          {rep.phone && <span>{rep.phone}</span>}
          {rep.email && <span>{rep.email}</span>}
          {rep.idNumber && (
            <span>
              {t('partner.representative.idNumber')}: {rep.idNumber}
            </span>
          )}
        </div>

        <div className="text-sm text-muted-foreground">
          {formatDate(rep.validFrom)}
          {' – '}
          {rep.validUntil ? formatDate(rep.validUntil) : t('partner.representative.unlimited')}
        </div>

        <PermissionBadges rep={rep} />

        {rep.notes && <p className="text-sm text-muted-foreground">{rep.notes}</p>}
      </div>

      {/* Actions */}
      <div className="flex shrink-0 gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleToggleActive}
          disabled={toggling || deleting}
          className="text-xs"
          title={
            rep.isActive
              ? t('partner.representative.deactivate')
              : t('partner.representative.activate')
          }
        >
          {rep.isActive
            ? t('partner.representative.deactivate')
            : t('partner.representative.activate')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setEditing(true)}
          disabled={deleting || toggling}
          className="text-xs"
        >
          {t('common.edit')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void handleDelete()}
          disabled={deleting || toggling}
          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
          aria-label={`${t('common.delete')} ${rep.name}`}
        >
          ✕
        </Button>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

interface RepresentativesTabProps {
  partnerId: string;
}

export function RepresentativesTab({ partnerId }: RepresentativesTabProps) {
  const { t } = useTranslation();
  const [representatives, setRepresentatives] = useState<Representative[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingNew, setAddingNew] = useState(false);
  const [savingNew, setSavingNew] = useState(false);

  const today = new Date().toISOString().substring(0, 10);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getRepresentatives(partnerId);
      setRepresentatives(data);
    } catch {
      setRepresentatives([]);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAddSave = useCallback(
    async (form: RepFormState) => {
      setSavingNew(true);
      try {
        await createRepresentative(partnerId, formToInput(form));
        setAddingNew(false);
        void load();
      } catch {
        // silent
      } finally {
        setSavingNew(false);
      }
    },
    [partnerId, load]
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('partner.representative.title')}</CardTitle>
        {!addingNew && (
          <Button type="button" variant="outline" size="sm" onClick={() => setAddingNew(true)}>
            + {t('partner.representative.add')}
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-0 p-0">
        {/* Add form */}
        {addingNew && (
          <div className="border-b p-4 dark:border-[#2d7d8a]">
            <RepForm
              initial={emptyForm(today)}
              saving={savingNew}
              onSave={form => void handleAddSave(form)}
              onCancel={() => setAddingNew(false)}
            />
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-solid border-primary border-r-transparent" />
          </div>
        ) : representatives.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground">
            {t('partner.representative.noRepresentatives')}
          </p>
        ) : (
          <div className="divide-y dark:divide-[#2d7d8a]">
            {representatives.map(rep => (
              <RepRow key={rep.id} rep={rep} partnerId={partnerId} onChanged={() => void load()} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
